import React, { useContext, useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import RegistrationTable from "./RegistrationTable";
import RegistrationCards from "./RegistrationCards";
import AdminRegistrationContext from "../../context/adminRegistration/adminRegistrationContext";
import { FaSpinner } from "react-icons/fa";
import RegistrationFilterSidebar from "./RegistrationFilterSidebar";
import RegistrationColumnManager from "./RegistrationColumnManager";
import SearchForm from "./SearchForm";
import ExportExcelButton from "./ExportButton";

const RegistrationFormList = () => {
  const adminRegistrationContext = useContext(AdminRegistrationContext);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false);
  const {
    registrations,
    currentPage,
    pageSize,
    totalCount,
    loading,
    searchText,
    setCurrentPage,
    appliedFilters,
    getRegistrations,
    filteredRegistrations,
    listView,
    setListView,
  } = adminRegistrationContext;

    
  
  // Load initial data with default date range (last month)
  useEffect(() => {
    if (!registrations || registrations.length === 0) {

      // Calculate default date range (last month)
      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(today.getMonth() - 1);

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const initialFilters = {
        page: 1,
        pageSize: pageSize,
        startDate: formatDate(oneMonthAgo),
        endDate: formatDate(today)
      };

      getRegistrations(initialFilters);
    }
  }, []);
  
  
  
  // Use backend data as primary source
  const itemContent = registrations || filteredRegistrations || [];

  const totalPages = Math.ceil(totalCount / pageSize);
  const startRecord = ((currentPage - 1) * pageSize) + 1;
  const endRecord = Math.min(currentPage * pageSize, totalCount);


  // Get current filters for pagination
  const getCurrentFilters = () => {
    const baseFilters = {
      pageSize,
    };

    if (appliedFilters) {
      if (appliedFilters.status) baseFilters.status = appliedFilters.status;
      if (appliedFilters.learningCenterName) baseFilters.learningCenterName = appliedFilters.learningCenterName;
      if (appliedFilters.learningCenterCharter) baseFilters.learningCenterCharter = appliedFilters.learningCenterCharter;
      if (appliedFilters.age) baseFilters.age = appliedFilters.age;
      if (appliedFilters.startDate) baseFilters.startDate = appliedFilters.startDate;
      if (appliedFilters.endDate) baseFilters.endDate = appliedFilters.endDate;
    }

    if (searchText && searchText.trim() !== '') {
      baseFilters.searchText = searchText;
    }

    return baseFilters;
  };

  const handleNextPage = async () => {
    if (currentPage < totalPages && !loading) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      try {
        const filters = getCurrentFilters();
        await getRegistrations({
          ...filters,
          page: nextPage,
        });
      } catch (error) {
        console.error('Error fetching next page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  const handlePreviousPage = async () => {
    if (currentPage > 1 && !loading) {
      const prevPage = currentPage - 1;
      setCurrentPage(prevPage);

      try {
        const filters = getCurrentFilters();
        await getRegistrations({
          ...filters,
          page: prevPage,
        });
      } catch (error) {
        console.error('Error fetching previous page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  const handlePageClick = async (pageNumber) => {
    if (pageNumber !== currentPage && !loading && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);

      try {
        const filters = getCurrentFilters();
        await getRegistrations({
          ...filters,
          page: pageNumber,
        });
      } catch (error) {
        console.error('Error fetching page:', error);
        setCurrentPage(currentPage);
      }
    }
  };

  useEffect(() => {
    const isSearchResult = searchText && searchText.trim() !== '';
    const isInvalidSearchPage = isSearchResult && itemContent.length === 0 && currentPage > 1;

    if (isInvalidSearchPage && totalCount > 0) {
      setCurrentPage(1);
    }
  }, [itemContent, currentPage, searchText, totalCount, setCurrentPage]);

  const showPagination = totalCount > pageSize && !loading && itemContent.length > 0;



  return (
    <div className="forms-list">
      {/* Header Section */}
      <div className="list-header">
        <div className="list-header-left">
          <h2 className="list-title">Registration Forms</h2>
          <p className="list-subtitle">Manage and review enrollment submissions</p>
        </div>
      </div>

      {/* Toolbar Section */}
      <div className="list-toolbar-container">
        <div className="list-toolbar">
          <SearchForm />

          <button
            className="toolbar-btn filter-btn"
            onClick={() => setIsFilterSidebarOpen(true)}
          >
            <FaIcons.FaFilter />
            Filters
          </button>

          <button
            className="toolbar-btn columns-btn"
            onClick={() => setIsColumnManagerOpen(true)}
          >
            <FaIcons.FaColumns />
            Columns
          </button>
          <div className="toolbar-btn-group">

          <button
            className={`toolbar-btn view-btn ${listView ? 'active' : ''}`}
            onClick={() => setListView(true)}
          >
            <FaIcons.FaList />
          </button>

          <button
            className={`toolbar-btn view-btn ${!listView ? 'active' : ''}`}
            onClick={() => setListView(false)}
          >
            <FaIcons.FaThLarge />
          </button>
          </div>

          <ExportExcelButton getCurrentFilters={getCurrentFilters} />
        </div>
      </div>

      {/* Record Count */}
      {loading ? (
        <p className="record-count">Loading...</p>
      ) : (
        <p className="record-count">
          {totalCount > 0
            ? `${startRecord}-${endRecord} of ${totalCount} Records`
            : '0 Records'
          }
        </p>
      )}

      {/* Filter Sidebar */}
      <RegistrationFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={() => setIsFilterSidebarOpen(false)}
      />

      {/* Column Manager */}
      <RegistrationColumnManager
        isOpen={isColumnManagerOpen}
        onClose={() => setIsColumnManagerOpen(false)}
      />

      {/* Loading State */}
      {loading ? (
        <div className="interest-complete">
          <FaSpinner className="done-icon" />
          <p>Loading Please wait...</p>
        </div>
      ) : (
        <>
          {/* Data Table or Cards or Empty State */}
          {itemContent.length > 0 ? (
            listView ? (
              <RegistrationTable items={itemContent} />
            ) : (
              <RegistrationCards items={itemContent} />
            )
          ) : (
            <div className="interest-complete">
              <FaIcons.FaCheckSquare className="done-icon" />
              <p>No records found matching your search criteria</p>
            </div>
          )}

          {/* Pagination Controls */}
          {showPagination && (
            <div
              className="progress-btns"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                marginTop: '20px'
              }}
            >
              <button
                className="circle-btn"
                onClick={handlePreviousPage}
                disabled={currentPage === 1 || loading}
                style={{
                  opacity: currentPage === 1 || loading ? 0.5 : 1,
                  cursor: currentPage === 1 || loading ? 'not-allowed' : 'pointer'
                }}
                aria-label="Previous page"
              >
                <FaIcons.FaChevronLeft className="progress-level-btn" />
              </button>

              <span style={{ margin: '0 15px', fontWeight: 'bold' }}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                className="circle-btn"
                onClick={handleNextPage}
                disabled={currentPage === totalPages || loading}
                style={{
                  opacity: currentPage === totalPages || loading ? 0.5 : 1,
                  cursor: currentPage === totalPages || loading ? 'not-allowed' : 'pointer'
                }}
                aria-label="Next page"
              >
                <FaIcons.FaChevronRight className="progress-level-btn" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RegistrationFormList;
